/**
 * iJewel3D model-viewer replacement for Shopify Strass theme.
 *
 * Based on the official iJewel3D Shopify integration code.
 * Adapted for themes that use:
 *
 *   <model-media>
 *     <model-viewer></model-viewer>
 *   </model-media>
 *
 * The Strass theme uses ModelMedia extends BaseMedia instead of
 * Dawn's ProductModel extends DeferredMedia, so the Dawn-specific
 * ProductModel implementation is intentionally omitted.
 *
 * Original iJewel file version: 26-07-2025
 */

(() => {
  window.iJewelSDKSettings = {
    webgi_version: "0.15.0",
    scene_settings: "",
    environment_map: "https://playground.ijewel3d.com/environment/studio.hdr",

    loading: {
      loadingTextHeader: "",
      showFileNames: false,
      backgroundOpacity: 0.5,
      background: "#ffffff",
      textColor: "#182240",
      logoImage: ""
    },

    ...(window.iJewelSDKSettings || {})
  };


  if (customElements.get("model-viewer")) {
    console.warn(
      "[iJewel3D] <model-viewer> is already registered. " +
      "Load product-model-ijewel.js before theme.js and before Google's model viewer."
    );

    return;
  }


  const getViewerRuntimeUrl = () => {
    const version = String(
      window.iJewelSDKSettings.webgi_version || "0.15.0"
    );

    if (version.includes("/")) {
      return version;
    }

    return `https://releases.ijewel3d.com/webgi/runtime/viewer-${version}.js`;
  };

  const loadIJewelRuntime = () => {
    if (window.CoreViewerApp) {
      return Promise.resolve();
    }

    if (window.iJewelRuntimePromise) {
      return window.iJewelRuntimePromise;
    }

    window.iJewelRuntimePromise = new Promise((resolve, reject) => {
      const runtimeUrl = getViewerRuntimeUrl();

      const existingScript = document.querySelector(
        'script[data-ijewel-runtime="true"]'
      );

      if (existingScript) {
        existingScript.addEventListener("load", resolve, { once: true });
        existingScript.addEventListener(
          "error",
          () => reject(
            new Error(`[iJewel3D] Unable to load runtime: ${runtimeUrl}`)
          ),
          { once: true }
        );

        return;
      }

      const viewerScript = document.createElement("script");

      viewerScript.src = runtimeUrl;
      viewerScript.async = true;
      viewerScript.dataset.ijewelRuntime = "true";

      viewerScript.addEventListener(
        "load",
        () => {
          if (!window.CoreViewerApp) {
            reject(
              new Error(
                "[iJewel3D] Runtime loaded, but CoreViewerApp is unavailable."
              )
            );

            return;
          }

          resolve();
        },
        { once: true }
      );

      viewerScript.addEventListener(
        "error",
        () => {
          reject(
            new Error(`[iJewel3D] Unable to load runtime: ${runtimeUrl}`)
          );
        },
        { once: true }
      );

      document.head.appendChild(viewerScript);
    });

    return window.iJewelRuntimePromise;
  };


  let globalViewer = null;
  let globalViewerInitialization = null;

  class ModelViewerWebGi extends HTMLElement {
    constructor() {
    super();

    this.viewer = null;
    this.view = null;
    this.container = null;
    this.modelLoaded = false;
    this.initializationStarted = false;
    this.loadCallbacks = [];
    this.loadError = null;
    }

    connectedCallback() {

    this.removeAttribute("data-shopify-feature");

    if (!this.container) {
        this.container = document.createElement("div");
        this.container.className = "ijewel-model-viewer__container";
        this.container.style.width = "100%";
        this.container.style.height = "100%";
        this.container.style.minHeight = "inherit";
        this.container.style.position = "relative";

        this.appendChild(this.container);
    }

    if (this.initializationStarted) {
        return;
    }

    this.initializationStarted = true;

    window.addEventListener("resize", () => this.resizeViewer());

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", () => this.resizeViewer());
    }

    this.initialize().catch((error) => {
        this.loadError = error;

        console.error("[iJewel3D] Viewer initialization failed:", error);

        this.dispatchEvent(
          new CustomEvent("ijewel-viewer-error", {
            bubbles: true,
            detail: {
              element: this,
              error
            }
          })
        );
      });
    }

    /**
     * Strass uses this callback from its ModelMedia player adapter.
     */
    doOnLoad(callback) {
      if (typeof callback !== "function") {
        return;
      }

      if (this.modelLoaded) {
        callback(this);
        return;
      }

      this.loadCallbacks.push(callback);
    }

    runLoadCallbacks() {
      const callbacks = [...this.loadCallbacks];

      this.loadCallbacks = [];

      callbacks.forEach((callback) => {
        try {
          callback(this);
        } catch (error) {
          console.error("[iJewel3D] onLoad callback failed:", error);
        }
      });
    }

    async createViewer() {
      if (globalViewer) {
        this.viewer = globalViewer;

        if (
          this.viewer.canvas &&
          this.viewer.canvas.parentElement !== this.container
        ) {
          this.container.appendChild(this.viewer.canvas);
        }

        if (
          this.viewer.scene &&
          typeof this.viewer.scene.removeSceneModels === "function"
        ) {
          this.viewer.scene.removeSceneModels();
        }

        return this.viewer;
      }

      if (globalViewerInitialization) {
        this.viewer = await globalViewerInitialization;

        if (
          this.viewer.canvas &&
          this.viewer.canvas.parentElement !== this.container
        ) {
          this.container.appendChild(this.viewer.canvas);
        }

        return this.viewer;
      }

      globalViewerInitialization = (async () => {
        const viewer = new window.CoreViewerApp({
          container: this.container
        });

        await viewer.initialize({
          caching: true,
          ground: true,
          bloom: true,
          enableDrop: false,
          importPopup: true,
          debug: false
        });

        const renderer = viewer.renderer;

        if (renderer) {
          const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

          if (typeof renderer.setPixelRatio === "function") {
            renderer.setPixelRatio(pixelRatio);
          }

          if (typeof renderer.setSize === "function") {
            const rect = this.container.getBoundingClientRect();

            renderer.setSize(
              Math.max(1, Math.round(rect.width)),
              Math.max(1, Math.round(rect.height)),
              false
            );
          }
        }

        viewer.canvas.style.width = "100%";
        viewer.canvas.style.height = "100%";
        viewer.canvas.style.display = "block";

        globalViewer = viewer;

        return viewer;
      })();

      try {
        this.viewer = await globalViewerInitialization;
      } finally {
        globalViewerInitialization = null;
      }

      return this.viewer;
    }

    async initialize() {
      await loadIJewelRuntime();
      await this.createViewer();

      const src = this.getAttribute("src");

      if (!src) {
        throw new Error(
          "[iJewel3D] The Shopify <model-viewer> element has no src attribute."
        );
      }

      this.refreshLoadingScreen();

      let environmentMapPromise = Promise.resolve(null);

      if (
        window.iJewelSDKSettings.environment_map &&
        window.iJewelSDKSettings.environment_map.length
      ) {
        environmentMapPromise =
          this.viewer.getManager().importer.importSinglePath(
            window.iJewelSDKSettings.environment_map
          );
      }

      const modelPromise = this.viewer.load(src);

      const [model, environmentMap] = await Promise.all([
        modelPromise,
        environmentMapPromise
      ]);

      this.refreshLoadingScreen();

      if (
        window.iJewelSDKSettings.scene_settings &&
        window.iJewelSDKSettings.scene_settings.length
      ) {
        await this.viewer.load(
          window.iJewelSDKSettings.scene_settings
        );
      }

      const cameraViewsPlugin =
        this.viewer.plugins &&
        this.viewer.plugins.CameraViews;

      if (
        cameraViewsPlugin &&
        typeof cameraViewsPlugin.getCurrentCameraView === "function"
      ) {
        this.view = cameraViewsPlugin.getCurrentCameraView();
      }

      this.refreshLoadingScreen();

      if (
        environmentMap &&
        this.viewer.scene &&
        typeof this.viewer.scene.setEnvironment === "function"
      ) {
        this.viewer.scene.setEnvironment(environmentMap);
      }

      this.modelLoaded = true;

      this.resizeViewer();
      window.setTimeout(() => this.resizeViewer(), 100);
      window.setTimeout(() => this.resizeViewer(), 500);

      window.setTimeout(() => {
        this.runLoadCallbacks();

        window.dispatchEvent(
          new CustomEvent("ijewel-viewer-loaded", {
            detail: this
          })
        );

        this.dispatchEvent(
          new CustomEvent("ijewel-viewer-loaded", {
            bubbles: true,
            detail: {
              element: this,
              viewer: this.viewer,
              model
            }
          })
        );
      }, 200);
    }

    refreshLoadingScreen() {
      if (!this.viewer) {
        return;
      }

      const loadingSettings =
        window.iJewelSDKSettings.loading || {};

      const loadingPlugin =
        this.viewer.getPluginByType &&
        this.viewer.getPluginByType("LoadingScreenPlugin");

      if (!loadingPlugin) {
        return;
      }

      Object.entries(loadingSettings).forEach(([key, value]) => {
        if (key in loadingPlugin) {
          loadingPlugin[key] = value;
        }
      });
    }

    async resetView() {
      if (!this.view || !this.viewer) {
        return;
      }

      const cameraViewsPlugin =
        this.viewer.plugins &&
        this.viewer.plugins.CameraViews;

      if (
        cameraViewsPlugin &&
        typeof cameraViewsPlugin.animateToView === "function"
      ) {
        await cameraViewsPlugin.animateToView(this.view, 500);
      }
    }

    play() {
      this.setAttribute("playing", "");
    }

    pause() {
      this.removeAttribute("playing");
    }

    resizeViewer() {
      if (!this.viewer || !this.container) return;

      const rect = this.container.getBoundingClientRect();

      if (rect.width <= 0 || rect.height <= 0) return;

      const canvas = this.viewer.canvas;

      if (canvas) {
        canvas.style.width = "100%";
        canvas.style.height = "100%";
      }

      const renderer = this.viewer.renderer;

      if (renderer) {
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

        if (typeof renderer.setPixelRatio === "function") {
          renderer.setPixelRatio(pixelRatio);
        }

        if (typeof renderer.setSize === "function") {
          renderer.setSize(
            Math.max(1, Math.round(rect.width)),
            Math.max(1, Math.round(rect.height)),
            false
          );
        }
      }

      window.dispatchEvent(new Event("resize"));
    }
  }

  customElements.define("model-viewer", ModelViewerWebGi);
})();